import { Vec2, LineSegment } from '../utils/math';
import { Ray, Polarization, PX_TO_METERS } from './Ray';
import { wavelengthToChannel } from '../utils/color';

export type ComponentType = 'laser' | 'mirror' | 'lens' | 'prism' | 'glass' | 'curved_mirror' | 'aperture' | 'beam_splitter' | 'detector' | 'diffraction_grating' | 'medium' | 'polarizer';

/** Result of a ray hitting a component surface */
export interface RayHitResult {
  ray: Ray;
}

/** Describes a surface hit for handleRayHit */
export interface SurfaceHit {
  t: number;
  point: Vec2;
  normal: Vec2;
  component: Component;
  segmentIndex: number;
  entering: boolean;
}

/** Parameter definition for the UI panel */
export interface ParameterDef {
  key: string;
  label: string;
  type: 'slider' | 'checkbox';
  // slider fields
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  /** Convert component value to display value */
  toDisplay?: (comp: Component) => number;
  /** Convert display value back to component value */
  fromDisplay?: (value: number) => number;
  // checkbox fields
  toChecked?: (comp: Component) => boolean;
}

/** Drawing configuration for RayRenderer */
export interface DrawConfig {
  fillColor: string;
  strokeColor: string;
  selectedStrokeColor: string;
}

export abstract class Component {
  private static nextId = 1;

  static resetIdCounter(startFrom: number = 1): void {
    Component.nextId = startFrom;
  }

  readonly id: number;
  position: Vec2;
  angle: number; // radians
  readonly type: ComponentType;

  constructor(type: ComponentType, position: Vec2, angle: number = 0, id?: number) {
    this.id = id ?? Component.nextId++;
    if (this.id >= Component.nextId) {
      Component.nextId = this.id + 1;
    }
    this.type = type;
    this.position = position;
    this.angle = angle;
  }

  abstract getSegments(): LineSegment[];
  abstract getBounds(): { x: number; y: number; width: number; height: number };

  /** Get outward-facing normals for each segment returned by getSegments() */
  abstract getNormals(): Vec2[];

  /**
   * Handle a ray hitting this component's surface.
   * Returns new rays to trace (reflected, refracted, transmitted, etc.)
   */
  abstract handleRayHit(ray: Ray, hit: SurfaceHit): RayHitResult[];

  /** Whether the ray tracer should test intersections with this component */
  isOpticalSurface(): boolean {
    return true;
  }

  /** Get parameter definitions for the UI panel */
  abstract getParameterDefs(): ParameterDef[];

  /** Set a parameter by key from a display value */
  abstract setParameter(key: string, value: number | boolean): void;

  /** Serialize to a plain object for save/load */
  abstract serialize(): Record<string, unknown>;

  /** Get the default draw config */
  abstract getDrawConfig(): DrawConfig;
}

// ─── Component Registry ───

type ComponentDeserializer = (data: Record<string, unknown>) => Component;
const deserializers = new Map<ComponentType, ComponentDeserializer>();

export function registerDeserializer(type: ComponentType, fn: ComponentDeserializer): void {
  deserializers.set(type, fn);
}

export function deserializeComponent(data: Record<string, unknown>): Component {
  const type = data.type as ComponentType;
  const fn = deserializers.get(type);
  if (!fn) throw new Error(`Unknown component type: ${type}`);
  return fn(data);
}

// ─── Helper: create child ray inheriting phase/coherence state ───

function childRay(
  parent: Ray, position: Vec2, direction: Vec2,
  intensity: number, polarization: Polarization,
  phaseShift: number = 0,
): Ray {
  return new Ray(
    position, direction, parent.wavelength,
    intensity, parent.sourceId, polarization,
    parent.phase + phaseShift, parent.pathLength,
    parent.coherent, parent.coherenceLength,
    parent.isWhiteLight,
  );
}

// ─── Helper: Snell's law refraction shared by Prism and GlassPlate ───

export function computeRefraction(
  ray: Ray,
  hit: SurfaceHit,
  materialIndex: number,
): RayHitResult[] {
  // Cauchy's equation for dispersion: n(λ) = A + B/λ²
  const wavelengthUM = ray.wavelength * 1e6;
  const dispersiveIndex = materialIndex + 0.00420 / (wavelengthUM * wavelengthUM);
  const n2 = hit.entering ? dispersiveIndex : 1.0;
  const n1 = hit.entering ? 1.0 : dispersiveIndex;
  const ratio = n1 / n2;

  const cosI = -ray.direction.dot(hit.normal);
  const sinI2 = 1 - cosI * cosI;
  const sinT2 = ratio * ratio * sinI2;

  if (sinT2 > 1) {
    // Total internal reflection — +π phase shift, polarization preserved
    const reflectedDir = ray.direction.reflect(hit.normal);
    return [{
      ray: childRay(ray, hit.point.add(reflectedDir.scale(0.01)), reflectedDir,
        ray.intensity, ray.polarization, Math.PI),
    }];
  }

  const cosT = Math.sqrt(1 - sinT2);

  // Full Fresnel equations for s and p polarization
  const Rs = ((n1 * cosI - n2 * cosT) / (n1 * cosI + n2 * cosT)) ** 2;
  const Rp = ((n2 * cosI - n1 * cosT) / (n2 * cosI + n1 * cosT)) ** 2;

  const pol = ray.polarization;
  const reflectance = pol.s * Rs + pol.p * Rp;

  // Refracted ray direction
  const refractedDir = ray.direction.scale(ratio)
    .add(hit.normal.scale(ratio * cosI - cosT));

  // Polarization of refracted ray
  const refractedPolS = pol.s * (1 - Rs);
  const refractedPolP = pol.p * (1 - Rp);
  const refractedPolSum = refractedPolS + refractedPolP;
  const refractedPol = refractedPolSum > 0
    ? { s: refractedPolS / refractedPolSum, p: refractedPolP / refractedPolSum }
    : { s: 0.5, p: 0.5 };

  const results: RayHitResult[] = [{
    ray: childRay(ray, hit.point.add(refractedDir.scale(0.01)), refractedDir.normalize(),
      ray.intensity * (1 - reflectance), refractedPol, 0),
  }];

  // Reflected ray (if significant) — +π phase shift
  if (reflectance > 0.05) {
    const reflectedDir = ray.direction.reflect(hit.normal);
    const reflectedPolS = pol.s * Rs;
    const reflectedPolP = pol.p * Rp;
    const reflectedPolSum = reflectedPolS + reflectedPolP;
    const reflectedPol = reflectedPolSum > 0
      ? { s: reflectedPolS / reflectedPolSum, p: reflectedPolP / reflectedPolSum }
      : { s: 0.5, p: 0.5 };

    results.push({
      ray: childRay(ray, hit.point.add(reflectedDir.scale(0.01)), reflectedDir,
        ray.intensity * reflectance, reflectedPol, Math.PI),
    });
  }

  return results;
}

// ─── LaserSource ───

export class LaserSource extends Component {
  wavelength: number;
  power: number;
  whiteLight: boolean;
  beamWidth: number;         // 0 = single ray, >0 = parallel beam width in px
  rayCount: number;          // number of parallel rays across the beam width
  coherenceLength: number;   // max coherent path in px (0 = infinite)

  constructor(position: Vec2, angle: number = 0, wavelength: number = 650e-9, power: number = 1, whiteLight: boolean = false, id?: number, beamWidth: number = 0, rayCount: number = 1, coherenceLength?: number) {
    super('laser', position, angle, id);
    this.wavelength = wavelength;
    this.power = power;
    this.whiteLight = whiteLight;
    this.beamWidth = beamWidth;
    this.rayCount = rayCount;
    this.coherenceLength = coherenceLength ?? (whiteLight ? 50 : 5000);
  }

  getEmitOrigin(): Vec2 {
    return this.position;
  }

  getEmitDirection(): Vec2 {
    return Vec2.fromAngle(this.angle);
  }

  getSegments(): LineSegment[] {
    const dir = Vec2.fromAngle(this.angle);
    const perp = dir.perpendicular();
    const halfLen = 15;
    const halfWidth = 6;
    const back = this.position.sub(dir.scale(halfLen));
    return [
      { start: back.add(perp.scale(halfWidth)), end: back.sub(perp.scale(halfWidth)) },
      { start: back.add(perp.scale(halfWidth)), end: this.position.add(perp.scale(halfWidth)) },
      { start: back.sub(perp.scale(halfWidth)), end: this.position.sub(perp.scale(halfWidth)) },
    ];
  }

  getNormals(): Vec2[] {
    return this.getSegments().map(seg => {
      const edge = seg.end.sub(seg.start);
      return edge.perpendicular().normalize();
    });
  }

  isOpticalSurface(): boolean {
    return false; // rays pass through laser housings
  }

  handleRayHit(_ray: Ray, _hit: SurfaceHit): RayHitResult[] {
    return []; // lasers don't interact with rays
  }

  getBounds() {
    return { x: this.position.x - 15, y: this.position.y - 6, width: 30, height: 12 };
  }

  getParameterDefs(): ParameterDef[] {
    const defs: ParameterDef[] = [
      {
        key: 'whiteLight', label: 'White Light', type: 'checkbox',
        toChecked: (c) => (c as LaserSource).whiteLight,
      },
    ];
    if (!this.whiteLight) {
      defs.push({
        key: 'wavelength', label: 'Wavelength', type: 'slider',
        min: 380, max: 780, step: 1, unit: 'nm',
        toDisplay: (c) => Math.round((c as LaserSource).wavelength * 1e9),
        fromDisplay: (v) => v * 1e-9,
      });
    }
    defs.push({
      key: 'power', label: 'Power', type: 'slider',
      min: 1, max: 100, step: 1, unit: '%',
      toDisplay: (c) => Math.round((c as LaserSource).power * 100),
      fromDisplay: (v) => v / 100,
    });
    defs.push({
      key: 'beamWidth', label: 'Beam Width', type: 'slider',
      min: 0, max: 40, step: 1, unit: 'px',
      toDisplay: (c) => (c as LaserSource).beamWidth,
      fromDisplay: (v) => v,
    });
    if (this.beamWidth > 0) {
      defs.push({
        key: 'rayCount', label: 'Ray Count', type: 'slider',
        min: 3, max: 15, step: 2, unit: '',
        toDisplay: (c) => (c as LaserSource).rayCount,
        fromDisplay: (v) => v,
      });
    }
    defs.push({
      key: 'coherenceLength', label: 'Coherence', type: 'slider',
      min: 0, max: 10000, step: 50, unit: 'px',
      toDisplay: (c) => (c as LaserSource).coherenceLength,
      fromDisplay: (v) => v,
    });
    return defs;
  }

  setParameter(key: string, value: number | boolean): void {
    switch (key) {
      case 'wavelength': this.wavelength = value as number; break;
      case 'power': this.power = value as number; break;
      case 'whiteLight': this.whiteLight = value as boolean; break;
      case 'beamWidth': this.beamWidth = value as number; if (this.beamWidth > 0 && this.rayCount < 3) this.rayCount = 7; break;
      case 'rayCount': this.rayCount = value as number; break;
      case 'coherenceLength': this.coherenceLength = value as number; break;
    }
  }

  serialize(): Record<string, unknown> {
    return {
      id: this.id, type: this.type, position: { x: this.position.x, y: this.position.y },
      angle: this.angle, wavelength: this.wavelength, power: this.power, whiteLight: this.whiteLight,
      beamWidth: this.beamWidth, rayCount: this.rayCount, coherenceLength: this.coherenceLength,
    };
  }

  getDrawConfig(): DrawConfig {
    return { fillColor: 'rgba(100,100,255,0.3)', strokeColor: '#6666ff', selectedStrokeColor: '#ff8844' };
  }
}

function pos(data: Record<string, unknown>): Vec2 {
  const p = data.position as { x: number; y: number } | undefined;
  return new Vec2(p?.x ?? 0, p?.y ?? 0);
}

registerDeserializer('laser', (data) => new LaserSource(
  pos(data), data.angle as number, data.wavelength as number,
  data.power as number, data.whiteLight as boolean, data.id as number | undefined,
  (data.beamWidth as number) || 0, (data.rayCount as number) || 1,
  data.coherenceLength as number | undefined,
));

// ─── FlatMirror ───

export class FlatMirror extends Component {
  length: number;
  reflectance: number;

  constructor(position: Vec2, angle: number = 0, length: number = 80, reflectance: number = 0.95, id?: number) {
    super('mirror', position, angle, id);
    this.length = length;
    this.reflectance = reflectance;
  }

  getSurface(): LineSegment {
    const dir = Vec2.fromAngle(this.angle);
    const half = dir.scale(this.length / 2);
    return { start: this.position.sub(half), end: this.position.add(half) };
  }

  getNormal(): Vec2 {
    return Vec2.fromAngle(this.angle).perpendicular();
  }

  getSegments(): LineSegment[] {
    return [this.getSurface()];
  }

  getNormals(): Vec2[] {
    return [this.getNormal()];
  }

  handleRayHit(ray: Ray, hit: SurfaceHit): RayHitResult[] {
    const reflectedDir = ray.direction.reflect(hit.normal);
    return [{
      ray: childRay(ray, hit.point.add(reflectedDir.scale(0.01)), reflectedDir,
        ray.intensity * this.reflectance, ray.polarization, Math.PI),
    }];
  }

  getBounds() {
    const seg = this.getSurface();
    const minX = Math.min(seg.start.x, seg.end.x);
    const minY = Math.min(seg.start.y, seg.end.y);
    const maxX = Math.max(seg.start.x, seg.end.x);
    const maxY = Math.max(seg.start.y, seg.end.y);
    return { x: minX, y: minY, width: maxX - minX || 4, height: maxY - minY || 4 };
  }

  getParameterDefs(): ParameterDef[] {
    return [
      {
        key: 'length', label: 'Length', type: 'slider',
        min: 20, max: 200, step: 1, unit: 'px',
        toDisplay: (c) => (c as FlatMirror).length,
        fromDisplay: (v) => v,
      },
      {
        key: 'reflectance', label: 'Reflectance', type: 'slider',
        min: 1, max: 100, step: 1, unit: '%',
        toDisplay: (c) => Math.round((c as FlatMirror).reflectance * 100),
        fromDisplay: (v) => v / 100,
      },
    ];
  }

  setParameter(key: string, value: number | boolean): void {
    switch (key) {
      case 'length': this.length = value as number; break;
      case 'reflectance': this.reflectance = value as number; break;
    }
  }

  serialize(): Record<string, unknown> {
    return {
      id: this.id, type: this.type, position: { x: this.position.x, y: this.position.y },
      angle: this.angle, length: this.length, reflectance: this.reflectance,
    };
  }

  getDrawConfig(): DrawConfig {
    return { fillColor: 'rgba(192,192,224,0.3)', strokeColor: '#c0c0e0', selectedStrokeColor: '#ff8844' };
  }
}

registerDeserializer('mirror', (data) => new FlatMirror(
  pos(data), data.angle as number, data.length as number,
  data.reflectance as number, data.id as number | undefined,
));

// ─── Lens ───

export class Lens extends Component {
  focalLength: number;
  aperture: number;

  constructor(position: Vec2, angle: number = 0, focalLength: number = 100, aperture: number = 60, id?: number) {
    super('lens', position, angle, id);
    this.focalLength = focalLength;
    this.aperture = aperture;
  }

  getSurface(): LineSegment {
    const perp = Vec2.fromAngle(this.angle).perpendicular();
    const half = perp.scale(this.aperture / 2);
    return { start: this.position.sub(half), end: this.position.add(half) };
  }

  getNormal(): Vec2 {
    return Vec2.fromAngle(this.angle);
  }

  getSegments(): LineSegment[] {
    return [this.getSurface()];
  }

  getNormals(): Vec2[] {
    return [this.getNormal()];
  }

  handleRayHit(ray: Ray, hit: SurfaceHit): RayHitResult[] {
    const surface = this.getSurface();
    const surfaceDir = surface.end.sub(surface.start).normalize();
    const offset = hit.point.sub(this.position);
    const h = offset.dot(surfaceDir);
    const deflection = -h / this.focalLength;

    const cos = Math.cos(deflection);
    const sin = Math.sin(deflection);
    const d = ray.direction;
    const newDir = new Vec2(
      d.x * cos - d.y * sin,
      d.x * sin + d.y * cos,
    ).normalize();

    // Thin lens phase: -2π * h² / (2f * wavelengthPx)
    const wavelengthPx = ray.wavelength / PX_TO_METERS;
    const lensPhase = -2 * Math.PI * h * h / (2 * this.focalLength * wavelengthPx);

    return [{
      ray: childRay(ray, hit.point.add(newDir.scale(0.01)), newDir,
        ray.intensity * 0.98, ray.polarization, lensPhase),
    }];
  }

  getBounds() {
    const seg = this.getSurface();
    const minX = Math.min(seg.start.x, seg.end.x);
    const minY = Math.min(seg.start.y, seg.end.y);
    const maxX = Math.max(seg.start.x, seg.end.x);
    const maxY = Math.max(seg.start.y, seg.end.y);
    return { x: minX - 4, y: minY, width: (maxX - minX) + 8, height: maxY - minY || 4 };
  }

  getParameterDefs(): ParameterDef[] {
    return [
      {
        key: 'focalLength', label: 'Focal Length', type: 'slider',
        min: -500, max: 500, step: 1, unit: 'px',
        toDisplay: (c) => (c as Lens).focalLength,
        fromDisplay: (v) => v,
      },
      {
        key: 'aperture', label: 'Aperture', type: 'slider',
        min: 20, max: 150, step: 1, unit: 'px',
        toDisplay: (c) => (c as Lens).aperture,
        fromDisplay: (v) => v,
      },
    ];
  }

  setParameter(key: string, value: number | boolean): void {
    switch (key) {
      case 'focalLength': this.focalLength = value as number; break;
      case 'aperture': this.aperture = value as number; break;
    }
  }

  serialize(): Record<string, unknown> {
    return {
      id: this.id, type: this.type, position: { x: this.position.x, y: this.position.y },
      angle: this.angle, focalLength: this.focalLength, aperture: this.aperture,
    };
  }

  getDrawConfig(): DrawConfig {
    return { fillColor: 'rgba(100,140,255,0.15)', strokeColor: '#88aaff', selectedStrokeColor: '#ff8844' };
  }
}

registerDeserializer('lens', (data) => new Lens(
  pos(data), data.angle as number, data.focalLength as number,
  data.aperture as number, data.id as number | undefined,
));

// ─── Prism ───

export class Prism extends Component {
  size: number;
  refractiveIndex: number;

  constructor(position: Vec2, angle: number = 0, size: number = 60, refractiveIndex: number = 1.52, id?: number) {
    super('prism', position, angle, id);
    this.size = size;
    this.refractiveIndex = refractiveIndex;
  }

  getVertices(): Vec2[] {
    const h = this.size * Math.sqrt(3) / 2;
    const verts = [
      new Vec2(0, -h * 2 / 3),
      new Vec2(-this.size / 2, h / 3),
      new Vec2(this.size / 2, h / 3),
    ];
    return verts.map(v => v.rotate(this.angle).add(this.position));
  }

  getSegments(): LineSegment[] {
    const v = this.getVertices();
    return [
      { start: v[0], end: v[1] },
      { start: v[1], end: v[2] },
      { start: v[2], end: v[0] },
    ];
  }

  getNormals(): Vec2[] {
    const segments = this.getSegments();
    return segments.map(seg => {
      const edge = seg.end.sub(seg.start);
      const normal = edge.perpendicular().normalize();
      const midpoint = seg.start.add(edge.scale(0.5));
      const toCenter = this.position.sub(midpoint);
      if (normal.dot(toCenter) > 0) return normal.negate();
      return normal;
    });
  }

  handleRayHit(ray: Ray, hit: SurfaceHit): RayHitResult[] {
    return computeRefraction(ray, hit, this.refractiveIndex);
  }

  getBounds() {
    const v = this.getVertices();
    const xs = v.map(p => p.x);
    const ys = v.map(p => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
  }

  getParameterDefs(): ParameterDef[] {
    return [
      {
        key: 'size', label: 'Size', type: 'slider',
        min: 20, max: 150, step: 1, unit: 'px',
        toDisplay: (c) => (c as Prism).size,
        fromDisplay: (v) => v,
      },
      {
        key: 'refractiveIndex', label: 'Refractive Index', type: 'slider',
        min: 100, max: 250, step: 1, unit: '/100',
        toDisplay: (c) => (c as Prism).refractiveIndex * 100,
        fromDisplay: (v) => v / 100,
      },
    ];
  }

  setParameter(key: string, value: number | boolean): void {
    switch (key) {
      case 'size': this.size = value as number; break;
      case 'refractiveIndex': this.refractiveIndex = value as number; break;
    }
  }

  serialize(): Record<string, unknown> {
    return {
      id: this.id, type: this.type, position: { x: this.position.x, y: this.position.y },
      angle: this.angle, size: this.size, refractiveIndex: this.refractiveIndex,
    };
  }

  getDrawConfig(): DrawConfig {
    return { fillColor: 'rgba(100,200,150,0.15)', strokeColor: '#88ddaa', selectedStrokeColor: '#ff8844' };
  }
}

registerDeserializer('prism', (data) => new Prism(
  pos(data), data.angle as number, data.size as number,
  data.refractiveIndex as number, data.id as number | undefined,
));

// ─── GlassPlate ───

export class GlassPlate extends Component {
  width: number;
  height: number;
  refractiveIndex: number;

  constructor(position: Vec2, angle: number = 0, width: number = 10, height: number = 60, refractiveIndex: number = 1.52, id?: number) {
    super('glass', position, angle, id);
    this.width = width;
    this.height = height;
    this.refractiveIndex = refractiveIndex;
  }

  getSegments(): LineSegment[] {
    const dir = Vec2.fromAngle(this.angle);
    const perp = dir.perpendicular();
    const hw = dir.scale(this.width / 2);
    const hh = perp.scale(this.height / 2);
    const corners = [
      this.position.add(hw).add(hh),
      this.position.add(hw).sub(hh),
      this.position.sub(hw).sub(hh),
      this.position.sub(hw).add(hh),
    ];
    return [
      { start: corners[0], end: corners[1] },
      { start: corners[1], end: corners[2] },
      { start: corners[2], end: corners[3] },
      { start: corners[3], end: corners[0] },
    ];
  }

  getNormals(): Vec2[] {
    const dir = Vec2.fromAngle(this.angle);
    const perp = dir.perpendicular();
    return [
      dir.clone(),
      perp.negate(),
      dir.negate(),
      perp.clone(),
    ];
  }

  handleRayHit(ray: Ray, hit: SurfaceHit): RayHitResult[] {
    return computeRefraction(ray, hit, this.refractiveIndex);
  }

  getBounds() {
    const segs = this.getSegments();
    const points = segs.flatMap(s => [s.start, s.end]);
    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
  }

  getParameterDefs(): ParameterDef[] {
    return [
      {
        key: 'width', label: 'Thickness', type: 'slider',
        min: 5, max: 50, step: 1, unit: 'px',
        toDisplay: (c) => (c as GlassPlate).width,
        fromDisplay: (v) => v,
      },
      {
        key: 'height', label: 'Height', type: 'slider',
        min: 20, max: 200, step: 1, unit: 'px',
        toDisplay: (c) => (c as GlassPlate).height,
        fromDisplay: (v) => v,
      },
      {
        key: 'refractiveIndex', label: 'Refractive Index', type: 'slider',
        min: 100, max: 250, step: 1, unit: '/100',
        toDisplay: (c) => (c as GlassPlate).refractiveIndex * 100,
        fromDisplay: (v) => v / 100,
      },
    ];
  }

  setParameter(key: string, value: number | boolean): void {
    switch (key) {
      case 'width': this.width = value as number; break;
      case 'height': this.height = value as number; break;
      case 'refractiveIndex': this.refractiveIndex = value as number; break;
    }
  }

  serialize(): Record<string, unknown> {
    return {
      id: this.id, type: this.type, position: { x: this.position.x, y: this.position.y },
      angle: this.angle, width: this.width, height: this.height, refractiveIndex: this.refractiveIndex,
    };
  }

  getDrawConfig(): DrawConfig {
    return { fillColor: 'rgba(150,150,200,0.15)', strokeColor: '#aaaacc', selectedStrokeColor: '#ff8844' };
  }
}

registerDeserializer('glass', (data) => new GlassPlate(
  pos(data), data.angle as number, data.width as number,
  data.height as number, data.refractiveIndex as number, data.id as number | undefined,
));

// ─── CurvedMirror ───

export class CurvedMirror extends Component {
  radius: number;       // radius of curvature (positive = concave, negative = convex)
  aperture: number;     // arc length extent
  reflectance: number;

  constructor(position: Vec2, angle: number = 0, radius: number = 150, aperture: number = 80, reflectance: number = 0.95, id?: number) {
    super('curved_mirror', position, angle, id);
    this.radius = radius;
    this.aperture = aperture;
    this.reflectance = reflectance;
  }

  /** Center of curvature circle */
  private getCircleCenter(): Vec2 {
    const normal = Vec2.fromAngle(this.angle).perpendicular();
    return this.position.add(normal.scale(this.radius));
  }

  /** Get the N line segments approximating the curved surface */
  getSegments(): LineSegment[] {
    const N = 12;
    const center = this.getCircleCenter();
    const absR = Math.abs(this.radius);
    // Half-angle subtended by the aperture
    const halfAngle = Math.asin(Math.min(1, this.aperture / (2 * absR)));

    // Base angle: direction from center to mirror position
    const baseAngle = Math.atan2(this.position.y - center.y, this.position.x - center.x);

    const segments: LineSegment[] = [];
    for (let i = 0; i < N; i++) {
      const a1 = baseAngle - halfAngle + (2 * halfAngle * i) / N;
      const a2 = baseAngle - halfAngle + (2 * halfAngle * (i + 1)) / N;
      segments.push({
        start: new Vec2(center.x + absR * Math.cos(a1), center.y + absR * Math.sin(a1)),
        end: new Vec2(center.x + absR * Math.cos(a2), center.y + absR * Math.sin(a2)),
      });
    }
    return segments;
  }

  getNormals(): Vec2[] {
    const center = this.getCircleCenter();
    return this.getSegments().map(seg => {
      const mid = seg.start.add(seg.end.sub(seg.start).scale(0.5));
      const outward = mid.sub(center).normalize();
      // For concave mirror (radius > 0), normal should point toward center (inward)
      // For convex mirror (radius < 0), normal points outward
      return this.radius > 0 ? outward.negate() : outward;
    });
  }

  handleRayHit(ray: Ray, hit: SurfaceHit): RayHitResult[] {
    // Use actual surface normal at hit point (radial direction from circle center)
    const center = this.getCircleCenter();
    let normal = hit.point.sub(center).normalize();
    if (this.radius > 0) normal = normal.negate(); // concave: normal points inward

    // Ensure normal faces the incoming ray
    if (ray.direction.dot(normal) > 0) normal = normal.negate();

    const reflectedDir = ray.direction.reflect(normal);
    return [{
      ray: childRay(ray, hit.point.add(reflectedDir.scale(0.01)), reflectedDir,
        ray.intensity * this.reflectance, ray.polarization, Math.PI),
    }];
  }

  getBounds() {
    const segs = this.getSegments();
    const points = segs.flatMap(s => [s.start, s.end]);
    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return { x: minX, y: minY, width: Math.max(...xs) - minX || 4, height: Math.max(...ys) - minY || 4 };
  }

  getParameterDefs(): ParameterDef[] {
    return [
      {
        key: 'radius', label: 'Radius', type: 'slider',
        min: -500, max: 500, step: 1, unit: 'px',
        toDisplay: (c) => (c as CurvedMirror).radius,
        fromDisplay: (v) => v,
      },
      {
        key: 'aperture', label: 'Aperture', type: 'slider',
        min: 20, max: 200, step: 1, unit: 'px',
        toDisplay: (c) => (c as CurvedMirror).aperture,
        fromDisplay: (v) => v,
      },
      {
        key: 'reflectance', label: 'Reflectance', type: 'slider',
        min: 1, max: 100, step: 1, unit: '%',
        toDisplay: (c) => Math.round((c as CurvedMirror).reflectance * 100),
        fromDisplay: (v) => v / 100,
      },
    ];
  }

  setParameter(key: string, value: number | boolean): void {
    switch (key) {
      case 'radius': this.radius = value as number; break;
      case 'aperture': this.aperture = value as number; break;
      case 'reflectance': this.reflectance = value as number; break;
    }
  }

  serialize(): Record<string, unknown> {
    return {
      id: this.id, type: this.type, position: { x: this.position.x, y: this.position.y },
      angle: this.angle, radius: this.radius, aperture: this.aperture, reflectance: this.reflectance,
    };
  }

  getDrawConfig(): DrawConfig {
    return { fillColor: 'rgba(192,192,224,0.3)', strokeColor: '#d0d0f0', selectedStrokeColor: '#ff8844' };
  }
}

registerDeserializer('curved_mirror', (data) => new CurvedMirror(
  pos(data), data.angle as number, data.radius as number,
  data.aperture as number, data.reflectance as number, data.id as number | undefined,
));

// ─── Aperture (Single/Double Slit) ───

export class Aperture extends Component {
  height: number;         // total barrier height
  slitWidth: number;      // width of each slit opening
  slitCount: number;      // 1 or 2
  slitSeparation: number; // center-to-center distance between slits (for double slit)

  constructor(
    position: Vec2, angle: number = 0,
    height: number = 120, slitWidth: number = 10,
    slitCount: number = 1, slitSeparation: number = 30,
    id?: number,
  ) {
    super('aperture', position, angle, id);
    this.height = height;
    this.slitWidth = slitWidth;
    this.slitCount = slitCount;
    this.slitSeparation = slitSeparation;
  }

  /**
   * Returns line segments for the opaque barrier parts only.
   * Gaps (slits) are NOT included — rays pass through gaps freely.
   */
  getSegments(): LineSegment[] {
    const dir = Vec2.fromAngle(this.angle);
    const perp = dir.perpendicular();
    const halfH = this.height / 2;

    if (this.slitCount === 1) {
      // Single slit: barrier with one gap in the middle
      const halfSlit = this.slitWidth / 2;
      const top = this.position.add(perp.scale(halfH));
      const slitTop = this.position.add(perp.scale(halfSlit));
      const slitBottom = this.position.sub(perp.scale(halfSlit));
      const bottom = this.position.sub(perp.scale(halfH));
      return [
        { start: top, end: slitTop },
        { start: slitBottom, end: bottom },
      ];
    } else {
      // Double slit: two gaps centered at +/- slitSeparation/2
      const halfSep = this.slitSeparation / 2;
      const halfSlit = this.slitWidth / 2;
      const top = this.position.add(perp.scale(halfH));
      const bottom = this.position.sub(perp.scale(halfH));
      // Upper slit centered at +halfSep
      const s1Top = this.position.add(perp.scale(halfSep + halfSlit));
      const s1Bot = this.position.add(perp.scale(halfSep - halfSlit));
      // Lower slit centered at -halfSep
      const s2Top = this.position.sub(perp.scale(halfSep - halfSlit));
      const s2Bot = this.position.sub(perp.scale(halfSep + halfSlit));
      return [
        { start: top, end: s1Top },    // top barrier
        { start: s1Bot, end: s2Top },   // middle barrier
        { start: s2Bot, end: bottom },  // bottom barrier
      ];
    }
  }

  getNormals(): Vec2[] {
    const normal = Vec2.fromAngle(this.angle);
    return this.getSegments().map(() => normal);
  }

  handleRayHit(_ray: Ray, _hit: SurfaceHit): RayHitResult[] {
    // Rays hitting the opaque barrier are absorbed
    return [];
  }

  getBounds() {
    const perp = Vec2.fromAngle(this.angle).perpendicular();
    const halfH = this.height / 2;
    const top = this.position.add(perp.scale(halfH));
    const bottom = this.position.sub(perp.scale(halfH));
    const xs = [top.x, bottom.x];
    const ys = [top.y, bottom.y];
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return { x: minX - 4, y: minY - 4, width: Math.max(...xs) - minX + 8, height: Math.max(...ys) - minY + 8 };
  }

  getParameterDefs(): ParameterDef[] {
    return [
      {
        key: 'height', label: 'Height', type: 'slider',
        min: 40, max: 300, step: 1, unit: 'px',
        toDisplay: (c) => (c as Aperture).height,
        fromDisplay: (v) => v,
      },
      {
        key: 'slitWidth', label: 'Slit Width', type: 'slider',
        min: 2, max: 50, step: 1, unit: 'px',
        toDisplay: (c) => (c as Aperture).slitWidth,
        fromDisplay: (v) => v,
      },
      {
        key: 'slitCount', label: 'Double Slit', type: 'checkbox',
        toChecked: (c) => (c as Aperture).slitCount === 2,
      },
      {
        key: 'slitSeparation', label: 'Slit Separation', type: 'slider',
        min: 10, max: 100, step: 1, unit: 'px',
        toDisplay: (c) => (c as Aperture).slitSeparation,
        fromDisplay: (v) => v,
      },
    ];
  }

  setParameter(key: string, value: number | boolean): void {
    switch (key) {
      case 'height': this.height = value as number; break;
      case 'slitWidth': this.slitWidth = value as number; break;
      case 'slitCount': this.slitCount = (value as boolean) ? 2 : 1; break;
      case 'slitSeparation': this.slitSeparation = value as number; break;
    }
  }

  serialize(): Record<string, unknown> {
    return {
      id: this.id, type: this.type, position: { x: this.position.x, y: this.position.y },
      angle: this.angle, height: this.height, slitWidth: this.slitWidth,
      slitCount: this.slitCount, slitSeparation: this.slitSeparation,
    };
  }

  getDrawConfig(): DrawConfig {
    return { fillColor: 'rgba(180,180,180,0.3)', strokeColor: '#b0b0b0', selectedStrokeColor: '#ff8844' };
  }
}

registerDeserializer('aperture', (data) => new Aperture(
  pos(data), data.angle as number, data.height as number,
  data.slitWidth as number, data.slitCount as number,
  data.slitSeparation as number, data.id as number | undefined,
));

// ─── BeamSplitter ───

export class BeamSplitter extends Component {
  length: number;
  splitRatio: number; // 0-1, fraction of intensity that is reflected

  constructor(position: Vec2, angle: number = Math.PI / 4, length: number = 80, splitRatio: number = 0.5, id?: number) {
    super('beam_splitter', position, angle, id);
    this.length = length;
    this.splitRatio = splitRatio;
  }

  getSurface(): LineSegment {
    const dir = Vec2.fromAngle(this.angle);
    const half = dir.scale(this.length / 2);
    return { start: this.position.sub(half), end: this.position.add(half) };
  }

  getSegments(): LineSegment[] {
    return [this.getSurface()];
  }

  getNormals(): Vec2[] {
    return [Vec2.fromAngle(this.angle).perpendicular()];
  }

  handleRayHit(ray: Ray, hit: SurfaceHit): RayHitResult[] {
    const reflectedDir = ray.direction.reflect(hit.normal);
    return [
      // Reflected — +π phase shift
      { ray: childRay(ray, hit.point.add(reflectedDir.scale(0.01)), reflectedDir,
          ray.intensity * this.splitRatio, ray.polarization, Math.PI) },
      // Transmitted — no phase shift
      { ray: childRay(ray, hit.point.add(ray.direction.scale(0.01)), ray.direction,
          ray.intensity * (1 - this.splitRatio), ray.polarization, 0) },
    ];
  }

  getBounds() {
    const seg = this.getSurface();
    const minX = Math.min(seg.start.x, seg.end.x);
    const minY = Math.min(seg.start.y, seg.end.y);
    const maxX = Math.max(seg.start.x, seg.end.x);
    const maxY = Math.max(seg.start.y, seg.end.y);
    return { x: minX, y: minY, width: maxX - minX || 4, height: maxY - minY || 4 };
  }

  getParameterDefs(): ParameterDef[] {
    return [
      {
        key: 'length', label: 'Length', type: 'slider',
        min: 20, max: 200, step: 1, unit: 'px',
        toDisplay: (c) => (c as BeamSplitter).length,
        fromDisplay: (v) => v,
      },
      {
        key: 'splitRatio', label: 'Reflect %', type: 'slider',
        min: 1, max: 99, step: 1, unit: '%',
        toDisplay: (c) => Math.round((c as BeamSplitter).splitRatio * 100),
        fromDisplay: (v) => v / 100,
      },
    ];
  }

  setParameter(key: string, value: number | boolean): void {
    switch (key) {
      case 'length': this.length = value as number; break;
      case 'splitRatio': this.splitRatio = value as number; break;
    }
  }

  serialize(): Record<string, unknown> {
    return {
      id: this.id, type: this.type, position: { x: this.position.x, y: this.position.y },
      angle: this.angle, length: this.length, splitRatio: this.splitRatio,
    };
  }

  getDrawConfig(): DrawConfig {
    return { fillColor: 'rgba(150,200,255,0.2)', strokeColor: '#99bbdd', selectedStrokeColor: '#ff8844' };
  }
}

registerDeserializer('beam_splitter', (data) => new BeamSplitter(
  pos(data), data.angle as number, data.length as number,
  data.splitRatio as number, data.id as number | undefined,
));

// ─── Detector/Screen ───

export interface DetectorBin {
  intensity: number;           // incoherent intensity accumulator
  wavelengthWeighted: number;  // for spectral display
  coherentAmplitudes: Map<number, { re: number; im: number }>; // keyed by wavelength channel
}

const DETECTOR_BINS = 64;

const DETECTOR_HISTORY_LENGTH = 200;

export class Detector extends Component {
  length: number;
  bins: DetectorBin[] = [];
  private binCount: number = DETECTOR_BINS;

  /** Rolling history of total intensity per sample (most recent at end) */
  intensityHistory: number[] = [];
  /** Peak intensity seen in the history (for normalization) */
  private historyPeak: number = 0;

  constructor(position: Vec2, angle: number = 0, length: number = 120, id?: number) {
    super('detector', position, angle, id);
    this.length = length;
    this.resetBins();
  }

  resetBins(): void {
    this.bins = Array.from({ length: this.binCount }, () => ({
      intensity: 0, wavelengthWeighted: 0, coherentAmplitudes: new Map(),
    }));
  }

  /** Sample the current total intensity and push to history.
   *  Call once per frame from the engine loop. */
  sampleIntensity(): void {
    let total = 0;
    for (let i = 0; i < this.bins.length; i++) {
      total += this.getComputedIntensity(i);
    }
    this.intensityHistory.push(total);
    if (this.intensityHistory.length > DETECTOR_HISTORY_LENGTH) {
      this.intensityHistory.shift();
    }
    if (total > this.historyPeak) this.historyPeak = total;
    // Slowly decay peak so display adapts to signal changes
    this.historyPeak *= 0.999;
    if (this.historyPeak < 0.001) this.historyPeak = 0;
  }

  getHistoryPeak(): number {
    return this.historyPeak;
  }

  clearHistory(): void {
    this.intensityHistory = [];
    this.historyPeak = 0;
  }

  getSurface(): LineSegment {
    const perp = Vec2.fromAngle(this.angle).perpendicular();
    const half = perp.scale(this.length / 2);
    return { start: this.position.sub(half), end: this.position.add(half) };
  }

  getSegments(): LineSegment[] {
    return [this.getSurface()];
  }

  getNormals(): Vec2[] {
    return [Vec2.fromAngle(this.angle)];
  }

  handleRayHit(ray: Ray, hit: SurfaceHit): RayHitResult[] {
    const surface = this.getSurface();
    const surfaceDir = surface.end.sub(surface.start);
    const surfaceLen = surfaceDir.length();
    if (surfaceLen < 1e-6) return [];

    const offset = hit.point.sub(surface.start);
    const t = offset.dot(surfaceDir.normalize()) / surfaceLen;
    const binIdx = Math.floor(Math.max(0, Math.min(this.binCount - 1, t * this.binCount)));
    const bin = this.bins[binIdx];

    if (ray.coherent) {
      // Coherent: add complex amplitude for interference
      const channel = wavelengthToChannel(ray.wavelength);
      const amplitude = Math.sqrt(ray.intensity);
      const re = amplitude * Math.cos(ray.phase);
      const im = amplitude * Math.sin(ray.phase);

      const existing = bin.coherentAmplitudes.get(channel);
      if (existing) {
        existing.re += re;
        existing.im += im;
      } else {
        bin.coherentAmplitudes.set(channel, { re, im });
      }
    } else {
      // Incoherent: add intensity directly
      bin.intensity += ray.intensity;
    }

    bin.wavelengthWeighted += ray.wavelength * ray.intensity;
    return [];
  }

  /** Compute total intensity for a bin (coherent |amplitude|² + incoherent) */
  getComputedIntensity(binIdx: number): number {
    const bin = this.bins[binIdx];
    let total = bin.intensity;
    for (const [, amp] of bin.coherentAmplitudes) {
      total += amp.re * amp.re + amp.im * amp.im;
    }
    return total;
  }

  getMaxIntensity(): number {
    let max = 0;
    for (let i = 0; i < this.bins.length; i++) {
      const val = this.getComputedIntensity(i);
      if (val > max) max = val;
    }
    return max;
  }

  getSpectralData(): { wavelength: number; intensity: number }[] {
    return this.bins
      .filter((_, i) => this.getComputedIntensity(i) > 0)
      .map((b, i) => ({
        wavelength: b.wavelengthWeighted > 0 ? b.wavelengthWeighted / (b.intensity || 1) : 550e-9,
        intensity: this.getComputedIntensity(i),
      }));
  }

  getBounds() {
    const seg = this.getSurface();
    const minX = Math.min(seg.start.x, seg.end.x);
    const minY = Math.min(seg.start.y, seg.end.y);
    const maxX = Math.max(seg.start.x, seg.end.x);
    const maxY = Math.max(seg.start.y, seg.end.y);
    return { x: minX - 4, y: minY - 4, width: (maxX - minX) + 8, height: (maxY - minY) + 8 };
  }

  getParameterDefs(): ParameterDef[] {
    return [
      {
        key: 'length', label: 'Length', type: 'slider',
        min: 40, max: 300, step: 1, unit: 'px',
        toDisplay: (c) => (c as Detector).length,
        fromDisplay: (v) => v,
      },
    ];
  }

  setParameter(key: string, value: number | boolean): void {
    switch (key) {
      case 'length': this.length = value as number; this.resetBins(); break;
    }
  }

  serialize(): Record<string, unknown> {
    return {
      id: this.id, type: this.type, position: { x: this.position.x, y: this.position.y },
      angle: this.angle, length: this.length,
    };
  }

  getDrawConfig(): DrawConfig {
    return { fillColor: 'rgba(255,200,100,0.2)', strokeColor: '#ddaa44', selectedStrokeColor: '#ff8844' };
  }
}

registerDeserializer('detector', (data) => new Detector(
  pos(data), data.angle as number, data.length as number, data.id as number | undefined,
));

// ─── DiffractionGrating ───

const MAX_DIFFRACTION_ORDER = 5;
const GRATING_WHITE_SUBS: number = 3; // subdivide each white-light channel into 3 for ~8nm resolution

export class DiffractionGrating extends Component {
  length: number;        // physical extent
  lineDensity: number;   // lines per mm

  constructor(position: Vec2, angle: number = 0, length: number = 80, lineDensity: number = 600, id?: number) {
    super('diffraction_grating', position, angle, id);
    this.length = length;
    this.lineDensity = lineDensity;
  }

  /** Grating spacing in meters */
  private get gratingSpacing(): number {
    return 1e-3 / this.lineDensity; // convert lines/mm to m
  }

  getSurface(): LineSegment {
    const dir = Vec2.fromAngle(this.angle);
    const half = dir.scale(this.length / 2);
    return { start: this.position.sub(half), end: this.position.add(half) };
  }

  getSegments(): LineSegment[] {
    return [this.getSurface()];
  }

  getNormals(): Vec2[] {
    return [Vec2.fromAngle(this.angle).perpendicular()];
  }

  handleRayHit(ray: Ray, hit: SurfaceHit): RayHitResult[] {
    const results: RayHitResult[] = [];
    const d = this.gratingSpacing;
    const normal = hit.normal;
    const tangent = normal.perpendicular();

    const cosI = -ray.direction.dot(normal);
    const sinI = Math.sqrt(1 - cosI * cosI);
    const sinISign = ray.direction.dot(tangent) > 0 ? 1 : -1;
    const thetaI = sinISign * sinI;

    // Build wavelength list: subdivide white-light channels for finer spectral resolution
    const wavelengths: { wl: number; intensity: number }[] = [];
    if (ray.isWhiteLight) {
      const bandWidth = 25e-9; // ~25nm per white-light channel
      const halfBand = bandWidth / 2;
      for (let s = 0; s < GRATING_WHITE_SUBS; s++) {
        const t = GRATING_WHITE_SUBS === 1 ? 0 : (s / (GRATING_WHITE_SUBS - 1)) * 2 - 1;
        wavelengths.push({
          wl: ray.wavelength + t * halfBand,
          intensity: ray.intensity / GRATING_WHITE_SUBS,
        });
      }
    } else {
      wavelengths.push({ wl: ray.wavelength, intensity: ray.intensity });
    }

    for (const { wl, intensity: baseIntensity } of wavelengths) {
      for (let m = -MAX_DIFFRACTION_ORDER; m <= MAX_DIFFRACTION_ORDER; m++) {
        const sinThetaM = thetaI + m * wl / d;
        if (Math.abs(sinThetaM) >= 1) continue;

        const cosThetaM = Math.sqrt(1 - sinThetaM * sinThetaM);
        const outDir = normal.negate().scale(cosThetaM).add(tangent.scale(sinThetaM));

        // Sinc-squared envelope
        let orderIntensity: number;
        if (m === 0) {
          orderIntensity = 0.4;
        } else {
          const x = m * Math.PI * 0.5;
          orderIntensity = Math.pow(Math.sin(x) / x, 2) * 0.6 / (2 * MAX_DIFFRACTION_ORDER);
        }

        // Create child ray with grating phase offset (m * 2π per period)
        const gratingRay = childRay(ray, hit.point.add(outDir.scale(0.01)), outDir.normalize(),
          baseIntensity * orderIntensity, ray.polarization, 0);
        // Override wavelength for subdivided channels
        gratingRay.wavelength = wl;
        results.push({ ray: gratingRay });
      }
    }

    return results;
  }

  getBounds() {
    const seg = this.getSurface();
    const minX = Math.min(seg.start.x, seg.end.x);
    const minY = Math.min(seg.start.y, seg.end.y);
    const maxX = Math.max(seg.start.x, seg.end.x);
    const maxY = Math.max(seg.start.y, seg.end.y);
    return { x: minX, y: minY, width: maxX - minX || 4, height: maxY - minY || 4 };
  }

  getParameterDefs(): ParameterDef[] {
    return [
      {
        key: 'length', label: 'Length', type: 'slider',
        min: 20, max: 200, step: 1, unit: 'px',
        toDisplay: (c) => (c as DiffractionGrating).length,
        fromDisplay: (v) => v,
      },
      {
        key: 'lineDensity', label: 'Lines/mm', type: 'slider',
        min: 100, max: 2000, step: 10, unit: '',
        toDisplay: (c) => (c as DiffractionGrating).lineDensity,
        fromDisplay: (v) => v,
      },
    ];
  }

  setParameter(key: string, value: number | boolean): void {
    switch (key) {
      case 'length': this.length = value as number; break;
      case 'lineDensity': this.lineDensity = value as number; break;
    }
  }

  serialize(): Record<string, unknown> {
    return {
      id: this.id, type: this.type, position: { x: this.position.x, y: this.position.y },
      angle: this.angle, length: this.length, lineDensity: this.lineDensity,
    };
  }

  getDrawConfig(): DrawConfig {
    return { fillColor: 'rgba(200,150,255,0.2)', strokeColor: '#bb88ee', selectedStrokeColor: '#ff8844' };
  }
}

registerDeserializer('diffraction_grating', (data) => new DiffractionGrating(
  pos(data), data.angle as number, data.length as number,
  data.lineDensity as number, data.id as number | undefined,
));

// ─── Medium (Absorption/Scattering) ───

export class Medium extends Component {
  width: number;
  height: number;
  absorptionCoeff: number;  // absorption per 100px of path length
  scatterCoeff: number;     // Rayleigh scatter strength (0 = none)

  constructor(position: Vec2, angle: number = 0, width: number = 80, height: number = 80,
    absorptionCoeff: number = 0.3, scatterCoeff: number = 0, id?: number) {
    super('medium', position, angle, id);
    this.width = width;
    this.height = height;
    this.absorptionCoeff = absorptionCoeff;
    this.scatterCoeff = scatterCoeff;
  }

  getSegments(): LineSegment[] {
    const dir = Vec2.fromAngle(this.angle);
    const perp = dir.perpendicular();
    const hw = dir.scale(this.width / 2);
    const hh = perp.scale(this.height / 2);
    const corners = [
      this.position.add(hw).add(hh),
      this.position.add(hw).sub(hh),
      this.position.sub(hw).sub(hh),
      this.position.sub(hw).add(hh),
    ];
    return [
      { start: corners[0], end: corners[1] },
      { start: corners[1], end: corners[2] },
      { start: corners[2], end: corners[3] },
      { start: corners[3], end: corners[0] },
    ];
  }

  getNormals(): Vec2[] {
    const dir = Vec2.fromAngle(this.angle);
    const perp = dir.perpendicular();
    return [dir.clone(), perp.negate(), dir.negate(), perp.clone()];
  }

  handleRayHit(ray: Ray, hit: SurfaceHit): RayHitResult[] {
    if (!hit.entering) {
      // Exiting: just pass through (attenuation was applied on entry)
      return [{
        ray: childRay(ray, hit.point.add(ray.direction.scale(0.01)), ray.direction,
          ray.intensity, ray.polarization, 0),
      }];
    }

    // Entering: estimate path length through the medium and apply Beer-Lambert
    const cosAngle = Math.abs(ray.direction.dot(hit.normal));
    const pathLength = cosAngle > 0.01 ? this.width / cosAngle : this.width * 100;

    // Wavelength-dependent absorption with optional Rayleigh scattering
    let alpha = this.absorptionCoeff;
    if (this.scatterCoeff > 0) {
      const lambdaNm = ray.wavelength * 1e9;
      // Rayleigh: ~1/lambda^4, normalized to 550nm
      alpha += this.scatterCoeff * Math.pow(550 / lambdaNm, 4);
    }

    // Beer-Lambert: I = I0 * exp(-alpha * path / 100)
    // path is in px, absorptionCoeff is per 100px
    const transmittance = Math.exp(-alpha * pathLength / 100);

    return [{
      ray: childRay(ray, hit.point.add(ray.direction.scale(0.01)), ray.direction,
        ray.intensity * transmittance, ray.polarization, 0),
    }];
  }

  getBounds() {
    const segs = this.getSegments();
    const points = segs.flatMap(s => [s.start, s.end]);
    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
  }

  getParameterDefs(): ParameterDef[] {
    return [
      {
        key: 'width', label: 'Width', type: 'slider',
        min: 20, max: 300, step: 1, unit: 'px',
        toDisplay: (c) => (c as Medium).width,
        fromDisplay: (v) => v,
      },
      {
        key: 'height', label: 'Height', type: 'slider',
        min: 20, max: 300, step: 1, unit: 'px',
        toDisplay: (c) => (c as Medium).height,
        fromDisplay: (v) => v,
      },
      {
        key: 'absorptionCoeff', label: 'Absorption', type: 'slider',
        min: 0, max: 200, step: 1, unit: '%',
        toDisplay: (c) => Math.round((c as Medium).absorptionCoeff * 100),
        fromDisplay: (v) => v / 100,
      },
      {
        key: 'scatterCoeff', label: 'Scatter', type: 'slider',
        min: 0, max: 100, step: 1, unit: '%',
        toDisplay: (c) => Math.round((c as Medium).scatterCoeff * 100),
        fromDisplay: (v) => v / 100,
      },
    ];
  }

  setParameter(key: string, value: number | boolean): void {
    switch (key) {
      case 'width': this.width = value as number; break;
      case 'height': this.height = value as number; break;
      case 'absorptionCoeff': this.absorptionCoeff = value as number; break;
      case 'scatterCoeff': this.scatterCoeff = value as number; break;
    }
  }

  serialize(): Record<string, unknown> {
    return {
      id: this.id, type: this.type, position: { x: this.position.x, y: this.position.y },
      angle: this.angle, width: this.width, height: this.height,
      absorptionCoeff: this.absorptionCoeff, scatterCoeff: this.scatterCoeff,
    };
  }

  getDrawConfig(): DrawConfig {
    return { fillColor: 'rgba(100,80,60,0.25)', strokeColor: '#887766', selectedStrokeColor: '#ff8844' };
  }
}

registerDeserializer('medium', (data) => new Medium(
  pos(data), data.angle as number, data.width as number,
  data.height as number, (data.absorptionCoeff as number) ?? 0.3,
  (data.scatterCoeff as number) ?? 0, data.id as number | undefined,
));

// ─── Polarizer ───

export class Polarizer extends Component {
  length: number;
  /** Transmission axis angle relative to component angle (radians, 0 = along component surface) */
  transmissionAxis: number;

  constructor(position: Vec2, angle: number = 0, length: number = 60, transmissionAxis: number = 0, id?: number) {
    super('polarizer', position, angle, id);
    this.length = length;
    this.transmissionAxis = transmissionAxis;
  }

  getSurface(): LineSegment {
    const dir = Vec2.fromAngle(this.angle);
    const half = dir.scale(this.length / 2);
    return { start: this.position.sub(half), end: this.position.add(half) };
  }

  getSegments(): LineSegment[] {
    return [this.getSurface()];
  }

  getNormals(): Vec2[] {
    return [Vec2.fromAngle(this.angle).perpendicular()];
  }

  handleRayHit(ray: Ray, hit: SurfaceHit): RayHitResult[] {
    // Malus's law: transmitted intensity depends on alignment between
    // ray polarization and the polarizer's transmission axis.
    //
    // For unpolarized light (s=p=0.5): transmits 50% and polarizes output.
    // For fully s-polarized hitting a p-axis polarizer: transmits 0%.
    //
    // Model: the transmission axis selects a mix of s and p.
    // transmissionAxis=0 → passes s-polarization (perpendicular to surface normal)
    // transmissionAxis=PI/2 → passes p-polarization (parallel to plane of incidence)
    const axisAngle = this.transmissionAxis;
    const cosA = Math.cos(axisAngle);
    const sinA = Math.sin(axisAngle);

    // Fraction of each polarization component that the axis transmits
    // axis at angle A transmits: cos²(A) of s + sin²(A) of p
    const transmittedIntensity = ray.polarization.s * cosA * cosA + ray.polarization.p * sinA * sinA;

    if (transmittedIntensity < 0.001) return []; // fully blocked

    // Output is polarized along the transmission axis
    const outPol: Polarization = { s: cosA * cosA, p: sinA * sinA };

    return [{
      ray: childRay(ray, hit.point.add(ray.direction.scale(0.01)), ray.direction,
        ray.intensity * transmittedIntensity, outPol, 0),
    }];
  }

  getBounds() {
    const seg = this.getSurface();
    const minX = Math.min(seg.start.x, seg.end.x);
    const minY = Math.min(seg.start.y, seg.end.y);
    const maxX = Math.max(seg.start.x, seg.end.x);
    const maxY = Math.max(seg.start.y, seg.end.y);
    return { x: minX - 4, y: minY - 4, width: (maxX - minX) + 8, height: (maxY - minY) + 8 };
  }

  getParameterDefs(): ParameterDef[] {
    return [
      {
        key: 'length', label: 'Length', type: 'slider',
        min: 20, max: 150, step: 1, unit: 'px',
        toDisplay: (c) => (c as Polarizer).length,
        fromDisplay: (v) => v,
      },
      {
        key: 'transmissionAxis', label: 'Axis Angle', type: 'slider',
        min: 0, max: 180, step: 1, unit: '\u00b0',
        toDisplay: (c) => Math.round((c as Polarizer).transmissionAxis * 180 / Math.PI),
        fromDisplay: (v) => v * Math.PI / 180,
      },
    ];
  }

  setParameter(key: string, value: number | boolean): void {
    switch (key) {
      case 'length': this.length = value as number; break;
      case 'transmissionAxis': this.transmissionAxis = value as number; break;
    }
  }

  serialize(): Record<string, unknown> {
    return {
      id: this.id, type: this.type, position: { x: this.position.x, y: this.position.y },
      angle: this.angle, length: this.length, transmissionAxis: this.transmissionAxis,
    };
  }

  getDrawConfig(): DrawConfig {
    return { fillColor: 'rgba(200,200,100,0.2)', strokeColor: '#cccc66', selectedStrokeColor: '#ff8844' };
  }
}

registerDeserializer('polarizer', (data) => new Polarizer(
  pos(data), data.angle as number, data.length as number,
  data.transmissionAxis as number, data.id as number | undefined,
));
